import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useOutletContext } from "react-router-dom"
import dayjs from "dayjs"
import Pagination from "../../../components/Listing/Pagination"
import EmptyState from "../../../components/Common/EmptyState"
import { ICONS } from "../../../constants/icons"
import CityHubTabHeader from "../CityHubTabHeader"
import { CreatorsTable } from "../CityHubCards"
import { CITY_HUB_CREATORS_PAGE_SIZE } from "../cityHubTabs"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { isSameUserId, nameWithYou } from "../../../helpers/userDisplay"
import { goToRequestInvite } from "../../PublicCity/publicCityPaths"

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_SORT_BY = "total_xp"
const DEFAULT_SORT_DIRECTION = "desc"
const MEMBER_ROLE_WITH = ["currentRoles.roleTypes"]
const SKELETON_ROWS = 6

const SORT_OPTIONS = [
  { value: "total_xp", label: "XP" },
  { value: "total_projects", label: "Projects" },
]

const formatMemberName = user =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.full_name ||
  user?.name ||
  user?.email ||
  "N/A"

const roleTypeName = role => {
  const types = role?.role_types || role?.roleTypes || []
  const type = types[0]
  return String(type?.public_name || type?.name || "").trim()
}

const pickMemberRole = user => {
  const roles = user?.current_roles || user?.currentRoles || []
  const ongoing = roles.find(role => role?.is_ongoing)
  const fromOngoing = roleTypeName(ongoing)
  if (fromOngoing) return fromOngoing
  const fromAny = roles.map(roleTypeName).find(Boolean)
  if (fromAny) return fromAny
  return "—"
}

const formatJoined = value => {
  if (!value) return "—"
  const date = dayjs(value)
  return date.isValid() ? date.format("MMM D, YYYY") : "—"
}

const extractMembersPage = payload => {
  const result = payload?.data?.result
  if (Array.isArray(result)) {
    return { list: result, total: result.length, page: 1 }
  }
  const list = Array.isArray(result?.data) ? result.data : []
  return {
    list,
    total: Number(result?.total || list.length) || 0,
    page: Number(result?.current_page || 1) || 1,
  }
}

const mapCreator = (user, currentUserId) => {
  const name = formatMemberName(user)
  return {
    id: user?.id,
    name: nameWithYou(name, isSameUserId(user?.id, currentUserId)),
    role: pickMemberRole(user),
    avatar: getUserAvatarUrl(user),
    xp: Number(user?.total_xp ?? user?.xp_points ?? 0) || 0,
    projects: Number(user?.total_projects ?? 0) || 0,
    joined: formatJoined(user?.created_at || user?.joined_at || user?.joined_on),
  }
}

const CreatorsTableSkeleton = () => (
  <div className="city-hub__creators-wrap" aria-hidden="true">
    <div className="city-hub__creators-table">
      <div className="city-hub__creators-head" role="row">
        <div role="columnheader">Creator Name</div>
        <div role="columnheader">Role</div>
        <div role="columnheader">XP</div>
        <div role="columnheader">Projects</div>
        <div role="columnheader">Joined</div>
        <div className="city-hub__creators-actions-head" role="columnheader">
          Actions
        </div>
      </div>
      {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
        <div
          key={`creator-skel-${index}`}
          className="city-hub__creators-row city-hub__creators-row--skeleton"
        >
          <div className="city-hub__creators-name">
            <div className="amok-skeleton city-hub__collab-skel-avatar" />
            <div className="amok-skeleton city-hub__collab-skel-line" />
          </div>
          <div className="amok-skeleton city-hub__collab-skel-line" />
          <div className="amok-skeleton city-hub__collab-skel-line" />
          <div className="amok-skeleton city-hub__collab-skel-line" />
          <div className="amok-skeleton city-hub__collab-skel-line" />
          <div className="amok-skeleton city-hub__collab-skel-line" />
        </div>
      ))}
    </div>
  </div>
)

const CityHubCreatorsTab = () => {
  const navigate = useNavigate()
  const currentUserId = useSelector(state => state.auth?.currentUser?.id)
  const { tab, city, isCityLoading = false, isPublic = false } =
    useOutletContext()
  const cityId = city?.id || null

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY)
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION)
  const [creators, setCreators] = useState([])
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [search])

  const fetchCreators = useCallback(
    async ({ page: pageToLoad = 1 } = {}) => {
      if (!cityId) {
        setCreators([])
        setTotalItems(0)
        setError(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const listUrl = isPublic
          ? endpoints.getPublicCityMembers(cityId)
          : endpoints.getCityMembers(cityId)
        const response = await get(listUrl, {
          params: {
            page: pageToLoad,
            per_page: CITY_HUB_CREATORS_PAGE_SIZE,
            search: debouncedSearch || "",
            sort_by: sortBy,
            sort_direction: sortDirection,
            "with[]": MEMBER_ROLE_WITH,
          },
        })

        if (!response?.data?.success) {
          const message = toastApiError(response, "Failed to load creators")
          setError(message)
          setCreators([])
          setTotalItems(0)
          return
        }

        const { list, total, page: currentPage } = extractMembersPage(response)
        setCreators(
          list
            .map(user => mapCreator(user, currentUserId))
            .filter(item => item.id),
        )
        setTotalItems(total)
        setPage(currentPage)
      } catch (err) {
        const message = toastApiError(err, "Failed to load creators")
        setError(message)
        setCreators([])
        setTotalItems(0)
      } finally {
        setIsLoading(false)
      }
    },
    [cityId, currentUserId, debouncedSearch, isPublic, sortBy, sortDirection],
  )

  useEffect(() => {
    setPage(1)
    fetchCreators({ page: 1 })
  }, [fetchCreators])

  const handlePageChange = nextPage => {
    setPage(nextPage)
    fetchCreators({ page: nextPage })
  }

  const filterConfig = useMemo(
    () => [
      {
        key: "sortBy",
        type: "sortBy",
        label: "Sort By",
        value: sortBy,
        defaultValue: DEFAULT_SORT_BY,
        onChange: next => {
          setSortBy(next || DEFAULT_SORT_BY)
          setPage(1)
        },
        options: SORT_OPTIONS,
      },
      {
        key: "sortDirection",
        type: "sortDirection",
        label: "Order",
        value: sortDirection,
        defaultValue: DEFAULT_SORT_DIRECTION,
        onChange: next => {
          setSortDirection(next || DEFAULT_SORT_DIRECTION)
          setPage(1)
        },
      },
    ],
    [sortBy, sortDirection],
  )

  const showSkeleton = isCityLoading || isLoading

  return (
    <>
      <CityHubTabHeader
        title={tab.pageTitle}
        subtitle={tab.pageSubtitle}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        searchPlaceholder={tab.searchPlaceholder}
        filterConfig={filterConfig}
        showFilter={tab.showFilter}
      />

      {showSkeleton ? <CreatorsTableSkeleton /> : null}

      {!showSkeleton && creators.length > 0 ? (
        <CreatorsTable
          creators={creators}
          onViewProfile={creator => {
            if (!creator?.id) return
            if (isPublic) {
              goToRequestInvite(
                navigate,
                {
                  from: "public-city",
                  cityId,
                  tab: "creators",
                  profileId: creator.id,
                  city,
                },
                { isAuthenticated: Boolean(currentUserId) },
              )
              return
            }
            navigate(`/public/profile/${creator.id}`)
          }}
        />
      ) : null}

      {!showSkeleton && creators.length === 0 ? (
        <EmptyState
          title={error ? "Unable to load creators" : "No creators found"}
          description={
            error ||
            (!cityId
              ? "Select a city in the banner to browse creators."
              : isPublic
                ? "Nothing matches this search yet. Request an invite to join this city."
                : "Nothing matches this search yet.")
          }
          actionText={isPublic ? "Request an Invite" : undefined}
          onActionClick={
            isPublic
              ? () =>
                  goToRequestInvite(
                    navigate,
                    { from: "public-city", cityId, tab: "creators", city },
                    { isAuthenticated: Boolean(currentUserId) },
                  )
              : undefined
          }
        />
      ) : null}

      {!showSkeleton && totalItems > 0 ? (
        <Pagination
          currentPage={page}
          totalItems={totalItems}
          pageSize={CITY_HUB_CREATORS_PAGE_SIZE}
          onPageChange={handlePageChange}
          itemLabel="Creators"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />
      ) : null}
    </>
  )
}

export default CityHubCreatorsTab
